import React, { createContext, useContext, useEffect, useState } from 'react';

import { Redirect, Route, Switch } from 'react-router-dom';
import SwalDialog, { exportSuccessDialog } from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

import { AuthContext } from 'components/providers/AuthContextProvider';
import { DEFAULT_CATEGORY_VIEW_PAGE_SIZE, DEFAULT_GLOBAL_PRODUCT_COLUMNS } from 'lib/constants';
import { getEndOfDayTimestamp, getStartOfDayTimestamp } from 'lib/utils/dateUtils';
import { GLOBAL_PRODUCTS, GLOBAL_PRODUCTS_DOWNLOAD_CSV, VIEWS } from 'lib/networking/endpoints';
import GlobalProductsTaxonomySplitView from 'components/global_products/classify/GlobalProductsTaxonomySplitView';
import { ProductFilterMode } from 'components/global_products/global_products_view/ProductFilterButtonGroup';
import ProductStatus from 'lib/enums/ProductStatus';
import ProtectedRoute from 'components/layout/ProtectedRoute';
import UserPermission from 'lib/enums/UserPermission';

const GlobalProductsContext = createContext({});

const DefaultGlobalProductsView = {
  name: 'Default Global Products View',
  columns: DEFAULT_GLOBAL_PRODUCT_COLUMNS.map(column => ({ name: column, visible: true })),
};

function GlobalProductsContainer() {
  const [allViews, setAllViews] = useState([DefaultGlobalProductsView]);
  const [view, setView] = useState(DefaultGlobalProductsView);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_CATEGORY_VIEW_PAGE_SIZE);
  const [searchFilters, setSearchFilters] = useState({});
  const [searchFiltersId, setSearchFiltersId] = useState({});
  const [selectedStatus, setSelectedStatus] = useState([ProductStatus.UNCATEGORIZED]);
  const [sortConfig, setSortConfig] = useState({ field: '', direction: 'desc' });
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [selectedIDs, setSelectedIDs] = useState([]);
  const [productFilterMode, setProductFilterMode] = useState(ProductFilterMode.ALL);
  const { homeUrl } = useContext(AuthContext);

  const { data: { data: views } = {}, loading, isLoaded: viewsLoaded } = useGet(VIEWS);
  const { postData: createView, loading: viewCreating } = usePost(
    VIEWS,
    () => {
      SwalDialog('success', 'View created successfully', 'Success', 'center');
    },
    error => {
      SwalDialog(
        'error',
        'An error occurred while creating the view: ' + error?.response?.data?.message,
        'Error',
        'center',
      );
    },
  );

  const {
    data: { data: { count: productCount } = {} } = {},
    loading: globalProductsCountLoading,
    error: errorLoadingGlobalProductsCount,
    refetch: refetchCount,
  } = useGet(
    GLOBAL_PRODUCTS,
    {
      filters: JSON.stringify({
        statuses: selectedStatus,
        start_date: getStartOfDayTimestamp(startDate),
        end_date: getEndOfDayTimestamp(endDate),
        ...searchFilters,
        ...searchFiltersId,
      }),
      count_only: true,
      show_onboarded_dp_products: productFilterMode === ProductFilterMode.LIVE_DP,
      show_mfr_products_with_cnd_id: productFilterMode === ProductFilterMode.LIVE_MFR,
      show_active_mfr_products: productFilterMode === ProductFilterMode.ACTIVE_MFR,
      show_all_product_statuses: productFilterMode === ProductFilterMode.ALL_STATUSES,
      show_dp_with_gmv: productFilterMode === ProductFilterMode.DP_WITH_GMV,
    },
    !viewsLoaded || (!endDate && startDate),
  );

  const {
    data: { data: { global_products: products } = {} } = {},
    loading: globalProductsLoading,
    error: errorLoadingGlobalProducts,
    refetch,
  } = useGet(
    GLOBAL_PRODUCTS,
    {
      limit: itemsPerPage,
      offset: itemsPerPage * (currentPage - 1),
      columns: view?.columns?.map(c => c.name),
      sort_by: sortConfig?.field,
      sort_direction: sortConfig?.direction,
      filters: JSON.stringify({
        statuses: selectedStatus,
        start_date: getStartOfDayTimestamp(startDate),
        end_date: getEndOfDayTimestamp(endDate),
        ...searchFilters,
        ...searchFiltersId,
      }),
      show_onboarded_dp_products: productFilterMode === ProductFilterMode.LIVE_DP,
      show_mfr_products_with_cnd_id: productFilterMode === ProductFilterMode.LIVE_MFR,
      show_active_mfr_products: productFilterMode === ProductFilterMode.ACTIVE_MFR,
      show_all_product_statuses: productFilterMode === ProductFilterMode.ALL_STATUSES,
      show_dp_with_gmv: productFilterMode === ProductFilterMode.DP_WITH_GMV,
    },
    !viewsLoaded || (!endDate && startDate),
  );

  const { postData: exportGlobalProducts, loading: productsExporting } = usePost(
    GLOBAL_PRODUCTS_DOWNLOAD_CSV,
    () => exportSuccessDialog(),
    () =>
      SwalDialog(
        'error',
        'An error occurred while trying to download the global products',
        'Error',
        'center',
      ),
  );

  const handleExportGlobalProducts = () =>
    exportGlobalProducts({
      columns: view?.columns?.map(c => c.name),
      sort_by: sortConfig?.field,
      sort_direction: sortConfig?.direction,
      filters: {
        statuses: selectedStatus,
        start_date: getStartOfDayTimestamp(startDate),
        end_date: getEndOfDayTimestamp(endDate),
        ...searchFilters,
        ...searchFiltersId,
      },
      show_onboarded_dp_products: productFilterMode === ProductFilterMode.LIVE_DP,
      show_mfr_products_with_cnd_id: productFilterMode === ProductFilterMode.LIVE_MFR,
      show_active_mfr_products: productFilterMode === ProductFilterMode.ACTIVE_MFR,
      show_all_product_statuses: productFilterMode === ProductFilterMode.ALL_STATUSES,
      show_dp_with_gmv: productFilterMode === ProductFilterMode.DP_WITH_GMV,
    });

  const onColumnSearch = (column, searchText, searchId) => {
    setSearchFilters(prevState => ({ ...prevState, [column]: searchText || '' }));
    if (column.includes('category') && !column.includes('vendor')) {
      setSearchFiltersId(prevState => ({ ...prevState, [`${column}_id`]: searchId || '' }));
    }
  };

  const handleSetSelectedStatus = status => {
    setDateRange([null, null]);
    setSelectedStatus(status);
  };

  const handleSetProductFilterMode = mode => {
    setProductFilterMode(mode);
    setCurrentPage(1);
    if (mode === ProductFilterMode.DP_WITH_GMV) {
      setSortConfig({ field: 'annualized_gmv', direction: 'desc' });
    } else if (productFilterMode === ProductFilterMode.DP_WITH_GMV) {
      setSortConfig({ field: '', direction: 'desc' });
    }
  };

  useEffect(() => {
    if (!views?.length) {
      return;
    }
    const vs = views.map(v => ({
      ...v,
      columns: v.columns.map(c => ({ name: c, visible: true })),
    }));
    setAllViews(vs);
    setView(vs.find(v => v.active));
  }, [views]);

  const viewsLoading = loading || viewCreating;
  return (
    <GlobalProductsContext.Provider
      value={{
        allViews,
        setAllViews,
        view,
        setView,
        createView,
        viewsLoading,
        products,
        productCount,
        globalProductsLoading,
        errorLoadingGlobalProducts,
        globalProductsCountLoading,
        errorLoadingGlobalProductsCount,
        refetch,
        refetchCount,
        onColumnSearch,
        handleSetSelectedStatus,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        setItemsPerPage,
        searchFilters,
        searchFiltersId,
        selectedStatus,
        sortConfig,
        setSortConfig,
        startDate,
        endDate,
        setDateRange,
        handleExportGlobalProducts,
        productsExporting,
        selectedIDs,
        setSelectedIDs,
        productFilterMode,
        setProductFilterMode: handleSetProductFilterMode,
      }}
    >
      <Switch>
        <ProtectedRoute
          path="/global-products/classify"
          component={<GlobalProductsTaxonomySplitView />}
          permissions={[UserPermission.CLASSIFY]}
          fallbackUrl={homeUrl}
        />
        <Route path="/global-products">
          <Redirect to="/global-products/classify" />
        </Route>
      </Switch>
    </GlobalProductsContext.Provider>
  );
}

export default GlobalProductsContainer;
export { DefaultGlobalProductsView, GlobalProductsContext };
